import { t } from '@/locales/i18n'
import store from '@/store'
import {
  PaymentMethod,
  type AllTierPlans,
  type CommonTierPlanInfo,
  type ExternalPlans,
  type GetPlanArgs,
  type GetPlanCostsArgs,
  type GmPlan,
  type GmPlans,
  type IntervalTypes,
  type MonthlyTierPlan,
  type PaymentPlan,
  type PlanDescriptor,
  type PlanIntervals,
  type Price,
  type SelectedTierPlansArgs,
  type Subscription,
  type TierPlanArgs,
  type YearlyTierPlan,
} from '@/types/paymentPlans'
import { type ApiPlan, isAdyenApiPlan } from '@/types/api/plans'
import * as R from 'remeda'
import { gameModes } from '@/helpers/constants'

const getPlan = ({ type, tier, interval, price_type, api_plans }: GetPlanArgs) => {
  const plan = api_plans.find(
    (i: ApiPlan) => i.type === type && i.tier === tier && i.interval === interval,
  )
  if (!plan) return null

  // old backend API implementation (v1/payments/subscription/plan):
  //  - returned list of prices of type discount, base, early bird and affiliate
  // new backend API implementation (v2/payments/subscription/plan):
  //  - returns a single price which is either type of base, discount, early bird or affiliate
  //  - there is a field base_price for the base price
  const getBasePrice = (price: Price) => {
    if (!price.base_price) return null
    return {
      ...price,
      price: price.base_price.price,
    }
  }

  const planPrice = isAdyenApiPlan(plan)
    ? (plan.price.type === price_type && plan.price) ||
      (price_type === 'BASE' && getBasePrice(plan.price))
    : plan.prices.find(i => i.type === price_type)
  if (!planPrice) return null

  const price = Number(planPrice.price)
  const discount = Number(planPrice.discount)
  const prorated_price = isAdyenApiPlan(plan) ? parseFloat(plan.price.prorated_price) : 0

  const external_plans = planPrice.external_plans.reduce<ExternalPlans>(
    (acc, i) => ({ ...acc, [i.provider.toLowerCase()]: i }),
    {},
  )

  return {
    type,
    tier,
    interval,
    price_type,
    price,
    prorated_price,
    discount,
    external_plans,
    id: plan.id,
  } as PaymentPlan
}

const getIntervalTypes = ({
  type,
  tier,
  interval,
  api_plans,
}: GetPlanCostsArgs): IntervalTypes => ({
  base: getPlan({ type, tier, interval, price_type: 'BASE', api_plans }),
  early_bird: getPlan({ type, tier, interval, price_type: 'EARLY_BIRD', api_plans }),
  affiliate: getPlan({ type, tier, interval, price_type: 'AFFILIATE', api_plans }),
  discount: getPlan({ type, tier, interval, price_type: 'DISCOUNT', api_plans }),
})

const getPlanDetails = ({ type, tier, interval, cheap, api_plans }: GetPlanCostsArgs) => {
  const intervalTypes = getIntervalTypes({ type, tier, interval, api_plans })

  const sortedPlans = R.pipe(
    R.values(intervalTypes) as PaymentPlan[],
    R.filter((i: PaymentPlan) => Boolean(i)),
    R.sortBy((i: PaymentPlan) => i.price),
  )
  return cheap ? sortedPlans[0] : R.last(sortedPlans)
}

const getPlanCosts = ({ type, tier, api_plans }: GetPlanCostsArgs): PlanIntervals => ({
  monthly: {
    cheap: getPlanDetails({ type, tier, interval: 'MONTHLY', cheap: true, api_plans }),
    costly: getPlanDetails({ type, tier, interval: 'MONTHLY', cheap: false, api_plans }),
    options: getIntervalTypes({ type, tier, interval: 'MONTHLY', api_plans }),
  },
  yearly: {
    cheap: getPlanDetails({ type, tier, interval: 'YEARLY', cheap: true, api_plans }),
    costly: getPlanDetails({ type, tier, interval: 'YEARLY', cheap: false, api_plans }),
    options: getIntervalTypes({ type, tier, interval: 'YEARLY', api_plans }),
  },
})

const getApiPlans = (): ApiPlan[] =>
  store.getters.api_get_data('api_payments_subscription_plan') || []

export const getPlans = () => {
  const api_plans = getApiPlans()

  const types = gameModes as (keyof GmPlans)[]
  return types.reduce((acc, type) => {
    acc[type] = {
      id: type,
      starter: getPlanCosts({ type: type.toUpperCase(), tier: 'STARTER', api_plans }),
      premium: getPlanCosts({ type: type.toUpperCase(), tier: 'PREMIUM', api_plans }),
      elite: getPlanCosts({ type: type.toUpperCase(), tier: 'ELITE', api_plans }),
    }
    return acc
  }, {} as GmPlans)
}

export const getTierPlans = ({ type, tier }: PlanDescriptor) => {
  const api_plans = getApiPlans()
  const planIntervals = getPlanCosts({
    type: type?.toUpperCase(),
    tier: tier?.toUpperCase(),
    api_plans,
  })

  if (!planIntervals) return []

  const getCommonInfo = (interval: keyof PlanIntervals): CommonTierPlanInfo => {
    const cheap = planIntervals[interval].cheap
    if (!cheap) return {} as CommonTierPlanInfo
    const priceType = cheap.price_type
    const name = interval.slice(0, -2)

    return {
      id: `${type}_${tier}_${interval}`,
      plan: cheap,
      rank:
        interval === 'monthly'
          ? cheap.tier === 'STARTER'
            ? 10
            : cheap.tier === 'PREMIUM'
              ? 20
              : cheap.tier === 'ELITE'
                ? 30
                : 0
          : cheap.tier === 'STARTER'
            ? 11
            : cheap.tier === 'PREMIUM'
              ? 22
              : cheap.tier === 'ELITE'
                ? 33
                : 0,
      time: t(`tier.options.${name}`),
      title: t(`tier.options.${interval}`),
      name: t(`tier.options.${interval}_plan`),
      full_name: t(`tier.options.${tier}_${name}`),
      earlyBird: priceType === 'EARLY_BIRD',
      affiliate: priceType === 'AFFILIATE',
      discount: priceType === 'DISCOUNT',
      bestValue: interval === 'yearly',
    }
  }
  const month = planIntervals.monthly
  const year = planIntervals.yearly
  if (!month.cheap || !month.costly || !year.cheap || !year.costly) return []

  const monthlyInfo = getCommonInfo('monthly')
  const monthlyCheap = month.cheap.price
  const monthlyCostly = month.costly.price
  const monthlyDiscount = month.cheap.discount
  const monthlyForeverDiscount = month.cheap.discount && !monthlyInfo.affiliate

  const yearlyInfo = getCommonInfo('yearly')
  const yearlyCheap = year.cheap.price
  const yearlyCostly = year.costly.price
  const yearlyDiscount = year.cheap.discount
  const yearlyForeverDiscount = year.cheap.discount && !yearlyInfo.affiliate

  const monthly: MonthlyTierPlan = {
    ...monthlyInfo,
    no_discount_month_price: monthlyDiscount ? monthlyCostly : null,
    month_price: monthlyCheap,
    next_month_price: monthlyForeverDiscount ? monthlyCheap : monthlyCostly,
    yearlySavings: Number(((monthlyCheap - yearlyCheap / 12) * 12).toFixed()),
  }

  const yearly: YearlyTierPlan = {
    ...yearlyInfo,
    no_discount_month_price: yearlyDiscount ? Number((yearlyCostly / 12).toFixed()) : null,
    month_price: Number((yearlyCheap / 12).toFixed()),
    year_price: yearlyCheap,
    next_year_price: yearlyForeverDiscount ? yearlyCheap : yearlyCostly,
    savings: `${(100 - (yearlyCheap / (monthlyCheap * 12)) * 100).toFixed()}%`,
    subtitle: `${t('tier.options.one_pament_of')} ${yearlyCheap} USD`,
  }
  return [monthly, yearly] as (MonthlyTierPlan | YearlyTierPlan)[]
}
export const getAllSubscriptions = (): Subscription[] | undefined => {
  const {
    user_info: { subs },
  } = store.getters
  return subs
}
export const getCurrentSubscription = (gm_type: string): Subscription | undefined => {
  const subs = getAllSubscriptions()
  if (!subs) return undefined

  return (
    subs.find((i: Subscription) => i.plan.type?.toLowerCase() === gm_type) ||
    subs.find((i: Subscription) => i.plan.type?.toLowerCase() === 'any') ||
    subs[0]
  )
}
export const getAllTierPlans = (gm_type: string): AllTierPlans => ({
  starter: getTierPlans({
    type: gm_type,
    tier: 'starter',
  }),
  premium: getTierPlans({
    type: gm_type,
    tier: 'premium',
  }),
  elite: getTierPlans({
    type: gm_type,
    tier: 'elite',
  }),
})
export const getSelectedPlan = ({
  type,
  tier,
  interval,
  plans,
}: SelectedTierPlansArgs): PaymentPlan | null => {
  if (!type || !plans[type as keyof GmPlans]) {
    return null
  }

  const gmPlan = plans[type as keyof GmPlans]
  if (!tier) {
    return (
      gmPlan.starter?.monthly?.cheap ||
      gmPlan.premium?.monthly?.cheap ||
      gmPlan.elite?.monthly?.cheap ||
      null
    )
  }

  const tierPlan = gmPlan[tier as keyof GmPlan] as PlanIntervals
  if (!tierPlan) {
    return null
  }
  if (!interval) {
    return tierPlan.monthly.cheap || null
  }

  const intervalPlan = tierPlan[interval as keyof PlanIntervals]
  return intervalPlan?.cheap || null
}
export const getSelectedTierPlan = ({
  type,
  tier,
  interval,
  selectedTierPlans,
}: TierPlanArgs): MonthlyTierPlan | YearlyTierPlan =>
  selectedTierPlans.find(
    (i: MonthlyTierPlan | YearlyTierPlan) =>
      i.plan.type.toLowerCase() === type &&
      i.plan.tier.toLowerCase() === tier &&
      i.plan.interval?.toLowerCase() === interval,
  )!

export const getCurrentTierPlan = (currentPlan: ApiPlan, allTierPlans: AllTierPlans) => {
  const subId = `${currentPlan.type.toLowerCase()}_${currentPlan.tier.toLowerCase()}_${currentPlan.interval.toLowerCase()}`
  return (
    allTierPlans[currentPlan.tier.toLowerCase() as keyof AllTierPlans]?.find(
      (i: MonthlyTierPlan | YearlyTierPlan) => i.id === subId,
    ) || {
      id: subId,
      plan: currentPlan,
      rank: 0,
      time: '',
      title: '',
      name: 'FREE',
      full_name: 'Free plan',
      earlyBird: false,
      affiliate: false,
      discount: false,
      bestValue: false,
      no_discount_month_price: null,
      month_price: 0,
      next_month_price: 0,
      yearlySavings: 0,
      year_price: 0,
      next_year_price: 0,
      savings: '0%',
      subtitle: '',
    }
  )
}

export const hasOnlyFreePlans = (subscriptions: Subscription[]) =>
  subscriptions.every(
    sub => sub.canceled || !sub.plan || sub.plan?.tier === 'FREE' || sub.plan?.tier === 'TRIAL',
  )

export const isUpgradingCurrentPlan = (currentPlan: ApiPlan) =>
  !['FREE', 'TRIAL'].includes(currentPlan.tier)

export const isCryptoPaymentEnabled = (currentPlan: ApiPlan) =>
  store.getters.user_has_flag('CONFIRMO') &&
  (!isUpgradingCurrentPlan(currentPlan) ||
    (store.getters.user_has_flag('CONFIRMO_UPGRADES') &&
      (currentPlan.provider === 'Confirmo' ||
        currentPlan.provider === 'Adyen' ||
        currentPlan.provider === 'Gtowizard')))

export const hasRecurringPayment = (currentPlan: ApiPlan) =>
  currentPlan.provider === 'Stripe' ||
  currentPlan.provider === 'Adyen' ||
  currentPlan.provider === 'Paypal'

export const isRecurringPaymentMethod = (paymentMethod?: PaymentMethod) => {
  // If no payment method is provided (legacy), we assume it's a recurring card payment
  if (!paymentMethod) return true

  const NON_RECURRING_PAYMENT_METHODS = [PaymentMethod.CRYPTO, PaymentMethod.ALIPAY, PaymentMethod.WECHAT]
  return !NON_RECURRING_PAYMENT_METHODS.includes(paymentMethod)
}
